import { motion } from "framer-motion";
import { COLLECTION_COPY } from "../../data/products";
import { getProductImage } from "../../data/images";

const ease = [0.16, 1, 0.3, 1];

// Per-collection Heena campaign art — real uploaded photography only,
// each collection given its own crop, mood and campaign line.
export const COLLECTION_ART = {
  everyday: { img: "/brand/heena-card.jpg", pos: "object-[50%_22%]", note: "Styled for Heena" },
  minimal: { img: "/brand/heena-square.jpg", pos: "object-[50%_20%]", note: "Grace in Every Detail" },
  charm: { img: "/brand/heena-story.jpg", pos: "object-[50%_25%]", note: "Little Stories, Beautifully Told" },
  statement: { img: "/brand/heena-hero-desktop.jpg", pos: "object-[58%_32%]", note: "Designed to be Remembered" },
  tennis: { img: "/brand/heena-square.jpg", pos: "object-[50%_24%]", note: "A Line of Light, Always" },
  pearl: { img: "/brand/heena-story.jpg", pos: "object-[50%_20%]", note: "Soft Elegance, Always" },
  festive: { img: "/brand/heena-hero-desktop.jpg", pos: "object-[60%_30%]", note: "For Life's Brighter Moments" },
  "evil-eye": { img: "/brand/heena-card.jpg", pos: "object-[50%_18%]", note: "A Quiet Guard, Beautifully Worn" },
  floral: { img: "/brand/heena-story.jpg", pos: "object-[50%_28%]", note: "In Full Bloom, Every Day" },
  "heart-love": { img: "/brand/heena-square.jpg", pos: "object-[50%_22%]", note: "Worn Close, Meant Closer" },
  premium: { img: "/brand/heena-hero-desktop.jpg", pos: "object-[62%_28%]", note: "The Finest of the House" },
};

export const CollectionBanner = ({ slug, count, image }) => {
  const copy = COLLECTION_COPY[slug];
  const art = COLLECTION_ART[slug];
  if (!copy || !art) return null;

  const custom = image ? getProductImage(image, `${copy.title} collection campaign`).src : null;
  const src = custom || art.img;
  const pos = custom ? "object-[50%_25%]" : art.pos;

  return (
    <section className="relative overflow-hidden border-b border-brand-sand bg-brand-cream" data-testid="collection-banner">
      <div className="relative lg:absolute lg:inset-y-0 lg:right-0 lg:w-[52%]">
        <motion.img
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease }}
          src={src}
          alt={custom ? `${copy.title} bracelet collection — campaign image` : `Heena, the HESHAURA muse — ${copy.title} bracelet collection campaign`}
          className={`h-56 w-full object-cover sm:h-72 lg:h-full ${pos}`}
          data-testid="collection-banner-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-cream via-brand-cream/10 to-transparent lg:bg-gradient-to-r lg:from-brand-cream lg:via-brand-cream/40 lg:via-30% lg:to-transparent" />
        {!custom && (
          <p className="absolute bottom-5 right-6 hidden font-serif text-lg italic text-brand-cream/90 drop-shadow-md lg:block" aria-hidden="true">
            {art.note}
          </p>
        )}
      </div>
      <div className="relative mx-auto flex max-w-7xl px-4 sm:px-6 lg:min-h-[400px] lg:items-center lg:px-8">
        <div className="max-w-xl py-10 lg:py-16">
          <motion.nav
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease }}
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-stone"
            data-testid="collection-banner-breadcrumb"
          >
            <a href="/" className="hover:text-brand-gold">Home</a>
            <span>/</span>
            <a href="/shop" className="hover:text-brand-gold">Shop Bracelets</a>
            <span>/</span>
            <span className="text-brand-charcoal">{copy.title}</span>
          </motion.nav>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease }}
            className="mt-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-gold"
          >
            <span className="h-px w-8 bg-brand-gold/60" aria-hidden="true" />
            The Collection
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease }}
            className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-brand-charcoal sm:text-5xl lg:text-6xl text-balance"
            data-testid="collection-banner-title"
          >
            {copy.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45, ease }}
            className="mt-4 font-serif text-lg italic text-brand-stone sm:text-xl"
            data-testid="collection-banner-tagline"
          >
            {copy.tagline}
          </motion.p>
          {count > 0 && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-gold"
              data-testid="collection-banner-count"
            >
              {count} hand-finished {count === 1 ? "bracelet" : "bracelets"}
            </motion.p>
          )}
        </div>
      </div>
    </section>
  );
};
